import React, { useState } from 'react';
import { X } from 'lucide-react';
import { createAdmin, Admin } from '../../api/ApiAdmin';
import { useTranslation } from '../../../TranslationContext';
import { AxiosError } from 'axios';

interface AdminFormData {
  fullName: string;
  email: string;
}

interface AddAdminModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdminCreated?: (admin: Admin) => void;
  onError?: (message: string) => void;
}

const AddAdminModal: React.FC<AddAdminModalProps> = ({ 
  isOpen, 
  onClose, 
  onAdminCreated,
  onError 
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<AdminFormData>({
    fullName: '',
    email: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [formErrors, setFormErrors] = useState<Partial<AdminFormData & { general?: string }>>({});

  if (!isOpen) return null;

  const validateForm = (): boolean => {
    const errors: Partial<AdminFormData> = {};
    if (!formData.fullName.trim()) {
      errors.fullName = t('full_name_required');
    }
    if (!formData.email.trim()) {
      errors.email = t('email_required');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = t('invalid_email_format');
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    setFormErrors({});

    try {
      const createdAdmin = await createAdmin({
        fullName: formData.fullName,
        email: formData.email
      });

      if (onAdminCreated) {
        onAdminCreated(createdAdmin);
      }

      setFormData({ fullName: '', email: '' });
      onClose();
    } catch (err) {
      let errorMessage = t('failed_to_create_admin'); // Message par défaut
      let translationKey = 'failed_to_create_admin';

      // Mapper les messages d'erreur de l'API aux clés de traduction
      if (err instanceof AxiosError && err.response?.data?.errorMessage) {
        const apiError = err.response.data.errorMessage;
        if (apiError === 'Un Admin avec ce mail existe déjà' || 
            apiError === 'An administrator with this email already exists' || 
            apiError === 'Ya existe un administrador con este correo electrónico') {
          translationKey = 'admin_email_exists';
          errorMessage = t('admin_email_exists');
        } else {
          errorMessage = apiError; // Utiliser le message brut si non mappé
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }

      // Associer l'erreur à l'email si elle est liée à l'email
      if (translationKey === 'admin_email_exists' || errorMessage.toLowerCase().includes('mail')) {
        setFormErrors({ email: errorMessage });
      } else {
        setFormErrors({ general: errorMessage });
      }

      if (onError) onError(errorMessage);
      console.error('Error creating administrator:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 backdrop-blur-sm bg-black/30 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-800">{t('add_administrator')}</h2>
          <button 
            onClick={onClose} 
            className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500"
            disabled={isLoading}
            aria-label={t('close_modal')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {formErrors.general && (
          <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md">
            {formErrors.general}
          </div>
        )}
        {formErrors.fullName && (
          <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md">
            {formErrors.fullName}
          </div>
        )}
        {formErrors.email && (
          <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md">
            {formErrors.email}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('full_name')} *</label>
            <input
              type="text"
              value={formData.fullName}
              onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
              className={`mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-slate-500 focus:border-slate-600 ${formErrors.fullName ? 'border-red-500' : ''}`}
              required
              disabled={isLoading}
              placeholder={t('enter_full_name')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">{t('email')} *</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className={`mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-slate-500 focus:border-slate-600 ${formErrors.email ? 'border-red-500' : ''}`}
              required
              disabled={isLoading}
              placeholder={t('enter_email_address')}
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500"
              disabled={isLoading}
            >
              {t('cancel')}
            </button>
            <button
              type="submit"
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-slate-700 hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading ? t('creating') : t('add_administrator_button')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddAdminModal;
// import React, { useState } from 'react';
// import { X } from 'lucide-react';
// import { createAdmin, Admin } from '../../api/ApiAdmin';
// import { useTranslation } from '../../../TranslationContext';
// import { AxiosError } from 'axios'; // Importez AxiosError pour typer correctement l'erreur

// interface AdminFormData {
//   fullName: string;
//   email: string;
// }

// interface AddAdminModalProps {
//   isOpen: boolean;
//   onClose: () => void;
//   onAdminCreated?: (admin: Admin) => void;
//   onError?: (message: string) => void;
// }

// const AddAdminModal: React.FC<AddAdminModalProps> = ({ 
//   isOpen, 
//   onClose, 
//   onAdminCreated,
//   onError 
// }) => {
//   const { t } = useTranslation();
//   const [formData, setFormData] = useState<AdminFormData>({
//     fullName: '',
//     email: ''
//   });
//   const [isLoading, setIsLoading] = useState(false);
//   const [formErrors, setFormErrors] = useState<Partial<AdminFormData & { general?: string }>>({}); // Ajout d'un champ général pour les erreurs non spécifiques

//   if (!isOpen) return null;

//   const validateForm = (): boolean => {
//     const errors: Partial<AdminFormData> = {};
//     if (!formData.fullName.trim()) {
//       errors.fullName = t('full_name_required');
//     }
//     if (!formData.email.trim()) {
//       errors.email = t('email_required');
//     } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
//       errors.email = t('invalid_email_format');
//     }
//     setFormErrors(errors);
//     return Object.keys(errors).length === 0;
//   };

//   const handleSubmit = async (e: React.FormEvent) => {
//     e.preventDefault();
//     if (!validateForm()) return;

//     setIsLoading(true);
//     setFormErrors({});

//     try {
//       const createdAdmin = await createAdmin({
//         fullName: formData.fullName,
//         email: formData.email
//       });

//       if (onAdminCreated) {
//         onAdminCreated(createdAdmin);
//       }

//       setFormData({ fullName: '', email: '' });
//       onClose();
//     } catch (err) {
//       let errorMessage = t('failed_to_create_admin'); // Message par défaut

//       // Vérifiez si l'erreur est une AxiosError et extrayez le message
//       if (err instanceof AxiosError && err.response?.data?.errorMessage) {
//         errorMessage = err.response.data.errorMessage; // Ex: "Un Admin avec ce mail existe déjà"
//       } else if (err instanceof Error) {
//         errorMessage = err.message;
//       }

//       // Associez l'erreur à l'email si elle est liée à l'email
//       if (errorMessage.toLowerCase().includes('mail')) {
//         setFormErrors({ email: errorMessage });
//       } else {
//         setFormErrors({ general: errorMessage }); // Erreur générale si non spécifique
//       }

//       if (onError) onError(errorMessage);
//       console.error('Error creating administrator:', err);
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   return (
//     <div className="fixed inset-0 backdrop-blur-sm bg-black/30 flex items-center justify-center z-50 p-4">
//       <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
//         <div className="flex justify-between items-center mb-4">
//           <h2 className="text-xl font-semibold text-gray-800">{t('add_administrator')}</h2>
//           <button 
//             onClick={onClose} 
//             className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500"
//             disabled={isLoading}
//             aria-label={t('close_modal')}
//           >
//             <X className="h-5 w-5" />
//           </button>
//         </div>

//         {formErrors.general && (
//           <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md">
//             {formErrors.general}
//           </div>
//         )}
//         {formErrors.fullName && (
//           <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md">
//             {formErrors.fullName}
//           </div>
//         )}
//         {formErrors.email && (
//           <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md">
//             {formErrors.email}
//           </div>
//         )}

//         <form onSubmit={handleSubmit} className="space-y-4">
//           <div>
//             <label className="block text-sm font-medium text-gray-700">{t('full_name')} *</label>
//             <input
//               type="text"
//               value={formData.fullName}
//               onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
//               className={`mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-slate-500 focus:border-slate-600 ${formErrors.fullName ? 'border-red-500' : ''}`}
//               required
//               disabled={isLoading}
//               placeholder={t('enter_full_name')}
//             />
//           </div>

//           <div>
//             <label className="block text-sm font-medium text-gray-700">{t('email')} *</label>
//             <input
//               type="email"
//               value={formData.email}
//               onChange={(e) => setFormData({ ...formData, email: e.target.value })}
//               className={`mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-slate-500 focus:border-slate-600 ${formErrors.email ? 'border-red-500' : ''}`}
//               required
//               disabled={isLoading}
//               placeholder={t('enter_email_address')}
//             />
//           </div>

//           <div className="flex justify-end space-x-3 pt-4">
//             <button
//               type="button"
//               onClick={onClose}
//               className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500"
//               disabled={isLoading}
//             >
//               {t('cancel')}
//             </button>
//             <button
//               type="submit"
//               className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-slate-700 hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 disabled:opacity-50"
//               disabled={isLoading}
//             >
//               {isLoading ? t('creating') : t('add_administrator_button')}
//             </button>
//           </div>
//         </form>
//       </div>
//     </div>
//   );
// };

// export default AddAdminModal;
// import React, { useState } from 'react';
// import { X } from 'lucide-react';
// import { createAdmin, Admin } from '../../api/ApiAdmin';
// import { useTranslation } from '../../../TranslationContext';

// interface AdminFormData {
//   fullName: string;
//   email: string;
// }

// interface AddAdminModalProps {
//   isOpen: boolean;
//   onClose: () => void;
//   onAdminCreated?: (admin: Admin) => void;
//   onError?: (message: string) => void;
// }

// const AddAdminModal: React.FC<AddAdminModalProps> = ({ 
//   isOpen, 
//   onClose, 
//   onAdminCreated,
//   onError 
// }) => {
//   const { t } = useTranslation();
//   const [formData, setFormData] = useState<AdminFormData>({
//     fullName: '',
//     email: ''
//   });
//   const [isLoading, setIsLoading] = useState(false);
//   const [formErrors, setFormErrors] = useState<Partial<AdminFormData>>({});

//   if (!isOpen) return null;

//   const validateForm = (): boolean => {
//     const errors: Partial<AdminFormData> = {};
//     if (!formData.fullName.trim()) {
//       errors.fullName = t('full_name_required');
//     }
//     if (!formData.email.trim()) {
//       errors.email = t('email_required');
//     } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
//       errors.email = t('invalid_email_format');
//     }
//     setFormErrors(errors);
//     return Object.keys(errors).length === 0;
//   };

//   const handleSubmit = async (e: React.FormEvent) => {
//     e.preventDefault();
//     if (!validateForm()) return;

//     setIsLoading(true);
//     setFormErrors({});

//     try {
//       const createdAdmin = await createAdmin({
//         fullName: formData.fullName,
//         email: formData.email
//       });

//       if (onAdminCreated) {
//         onAdminCreated(createdAdmin);
//       }

//       setFormData({ fullName: '', email: '' });
//       onClose();
//     } catch (err) {
//       const errorMessage = err instanceof Error ? err.message : t('failed_to_create_admin');
//       setFormErrors({ email: errorMessage });
//       if (onError) onError(errorMessage);
//       console.error('Error creating administrator:', err);
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   return (
//     <div className="fixed inset-0 backdrop-blur-sm bg-black/30 flex items-center justify-center z-50 p-4">
//       <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
//         <div className="flex justify-between items-center mb-4">
//           <h2 className="text-xl font-semibold text-gray-800">{t('add_administrator')}</h2>
//           <button 
//             onClick={onClose} 
//             className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500"
//             disabled={isLoading}
//             aria-label={t('close_modal')}
//           >
//             <X className="h-5 w-5" />
//           </button>
//         </div>

//         {formErrors.fullName && (
//           <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md">
//             {formErrors.fullName}
//           </div>
//         )}
//         {formErrors.email && (
//           <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md">
//             {formErrors.email}
//           </div>
//         )}

//         <form onSubmit={handleSubmit} className="space-y-4">
//           <div>
//             <label className="block text-sm font-medium text-gray-700">{t('full_name')} *</label>
//             <input
//               type="text"
//               value={formData.fullName}
//               onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
//               className={`mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-slate-500 focus:border-slate-600 ${formErrors.fullName ? 'border-red-500' : ''}`}
//               required
//               disabled={isLoading}
//               placeholder={t('enter_full_name')}
//             />
//           </div>

//           <div>
//             <label className="block text-sm font-medium text-gray-700">{t('email')} *</label>
//             <input
//               type="email"
//               value={formData.email}
//               onChange={(e) => setFormData({ ...formData, email: e.target.value })}
//               className={`mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-slate-500 focus:border-slate-600 ${formErrors.email ? 'border-red-500' : ''}`}
//               required
//               disabled={isLoading}
//               placeholder={t('enter_email_address')}
//             />
//           </div>

//           <div className="flex justify-end space-x-3 pt-4">
//             <button
//               type="button"
//               onClick={onClose}
//               className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500"
//               disabled={isLoading}
//             >
//               {t('cancel')}
//             </button>
//             <button
//               type="submit"
//               className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-slate-700 hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 disabled:opacity-50"
//               disabled={isLoading}
//             >
//               {isLoading ? t('creating') : t('add_administrator_button')}
//             </button>
//           </div>
//         </form>
//       </div>
//     </div>
//   );
// };

// export default AddAdminModal;
